import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import reducers from "./reducers";
import { serviceMiddleware } from "./services";
import { requestErrorMiddleware } from "./middlewares/requestErrorMiddleware";

let globalStore;

function initStore(initialState) {
  const isProduction = process.env.NODE_ENV === "production";

  const config = configureStore({
    reducer: reducers,
    // Configure DevTools based on environment
    devTools: !isProduction
      ? {
          // Full features in development
          trace: true, // Enable stack trace capture for debugging
          traceLimit: 25, // Capture up to 25 stack frames
          name: "Fundview", // App name in Redux DevTools
          maxAge: 50, // Limit action history for performance
        }
      : {
          // Minimal features in production - log actions only, no trace overhead
          trace: false, // Disable trace in production for performance
          name: "Fundview",
          maxAge: 25, // Reduced history in production
        },
    initialState,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck: false, // Only check in development
        immutableCheck: !isProduction, // Only check in development
      })
        .concat(requestErrorMiddleware) // Error handler runs first, before RTK Query middlewares
        .concat(serviceMiddleware),
  });

  setupListeners(config.dispatch);

  return config;
}

export const initializeStore = preloadedState => {
  let initialStore = globalStore ?? initStore(preloadedState);

  // After navigating to a page with an initial Redux state, merge that state
  // with the current state in the store, and create a new store
  if (preloadedState && globalStore) {
    initialStore = initStore({
      ...globalStore.getState(),
      ...preloadedState,
    });
    // Reset the current store
    globalStore = undefined;
  }

  // For SSG and SSR always create a new store
  if (typeof window === "undefined") return initialStore;
  // Create the store once in the client
  if (!globalStore) globalStore = initialStore;

  return initialStore;
};
